import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpdateSportInput } from '@proref/schemas';
import {
  listSports,
  listRules,
  createSport,
  updateSport,
  deleteSport,
} from '../services/sports.service';

// The sports catalogue. Key is ['sports'] per the query-key convention. `enabled` defaults to
// true (existing super_admin pages don't need to pass anything); set it to false to skip the
// call for a page that doesn't render the catalogue for the current role.
export function useSportsQuery(enabled = true) {
  return useQuery({
    queryKey: ['sports'],
    queryFn: listSports,
    enabled,
    // Near-static catalogue — a sport is added rarely, so don't re-request it while the user moves
    // around inside a session.
    staleTime: 60 * 60_000,
    // ...but ALWAYS revalidate on mount, ignoring that staleTime.
    //
    // Without this the hour interacts badly with the localStorage persister: the cache rehydrates
    // at boot still "fresh", so refetchOnMount's default (refetch only when stale) issues NO request
    // — and a page load, a hard reload, even reopening the browser all repaint the same rows for up
    // to an hour, with nothing the user can do about it. That is how a sport deleted straight in the
    // database (no mutation ran, so nothing invalidated ['sports']) kept showing in the console.
    // 'always' keeps the instant paint from cache and restores the silent background reconcile.
    refetchOnMount: 'always',
  });
}

// Add a sport, then refresh the catalogue so it appears immediately.
export function useCreateSport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSport,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sports'] });
    },
  });
}

// Edit a sport, then refresh the catalogue. Rules are keyed by slug, so if the slug changed the
// old ['rules', oldSlug] cache is simply left to go stale (gc'd) — the selector reloads by id.
export function useUpdateSport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSportInput }) => updateSport(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sports'] });
    },
  });
}

// Delete a sport, then refresh the catalogue so it drops out immediately.
export function useDeleteSport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSport,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sports'] });
    },
  });
}

// A sport's rule-book, keyed by slug. Disabled until a slug is selected so we don't
// fire a request for `undefined`.
export function useRulesQuery(slug: string | undefined) {
  return useQuery({
    queryKey: ['rules', slug],
    queryFn: () => listRules(slug as string),
    enabled: Boolean(slug),
    // The rule-book itself changes rarely — half-hour stale window is plenty within a session.
    staleTime: 30 * 60_000,
    // Same reason as useSportsQuery above: a long staleTime plus the persister means a reload would
    // otherwise never re-request, so an article edited or removed outside this tab could sit wrong
    // on screen for half an hour. Rulebooks are admin-edited content, so mount must reconcile.
    refetchOnMount: 'always',
  });
}
